import {
  ConfigProvider as AntdConfigProvider,
  type ConfigProviderProps,
  type ThemeConfig
} from 'antd'
import type { RenderEmptyHandler } from 'antd/es/config-provider'
import enUS from 'antd/locale/en_US'
import frBE from 'antd/locale/fr_BE'
import nlBE from 'antd/locale/nl_BE'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { type AntdTheme } from '@vivaldis/ui'
import { Empty } from '../Empty'

interface Props extends ConfigProviderProps {
  appTheme?: AntdTheme
}

export const ConfigProvider = ({ appTheme, ...props }: Props) => {
  const [, { language }] = useTranslation()

  const locale = useMemo(() => {
    if (language === 'nl') {
      return nlBE
    }
    if (language === 'fr') {
      return frBE
    }
    return enUS
  }, [language])

  const theme: ThemeConfig | undefined = useMemo(() => {
    // appTheme should be passed to ConfigProvider only on top level
    // in case when we want to customize only one or few component we don't need to pass theme
    if (!appTheme) {
      return undefined
    }

    return {
      token: {
        colorPrimary: appTheme.colorPrimary,
        colorLink: appTheme.colorLink,
        colorLinkHover: appTheme.colorLinkHover,
        colorLinkActive: appTheme.colorLinkActive,
        colorBgLayout: appTheme.colorBgLayout,
        fontFamily: "'Open Sans', sans-serif", //  default is: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'"
        colorTextBase: appTheme.gray9,
        colorText: appTheme.gray9,
        borderRadius: 8,
        colorTextLightSolid: appTheme.colorTextLightSolid // In Figma it's `colorTextLightSolidPrimary`
      },
      components: {
        Avatar: {
          lineWidth: 0 // border width = 0
        },
        Badge: {
          colorTextLightSolid: appTheme.white
        },
        Button: {
          colorText: appTheme.colorPrimary
        },
        Checkbox: {
          borderRadiusSM: 2,
          lineWidth: 2
        },
        Divider: {
          colorText: appTheme.gray5,
          marginLG: 16,
          colorSplit: appTheme.gray5
        },
        Form: {
          labelRequiredMarkColor: appTheme.orange6,
          colorTextHeading: appTheme.gray7,
          paddingXS: 4
        },
        Layout: {
          headerBg: appTheme.gray1, // it's "white" color
          controlHeight: 72 / 2,
          controlHeightLG: 24 / 1.25
        },
        Menu: {
          fontSize: 16,
          controlHeightLG: 48,
          marginXXS: 8,
          itemSelectedBg: appTheme.Menu.itemSelectedBg,
          itemSelectedColor: appTheme.Menu.itemSelectedColor,
          subMenuItemSelectedColor: appTheme.Menu.subMenuItemSelectedColor
        },
        Radio: {
          buttonColor: appTheme.Radio.buttonColor
        },
        Table: {
          borderRadiusLG: 6
        },
        Tooltip: {
          colorTextLightSolid: appTheme.Tooltip.colorTextLightSolid
        }
      }
    } satisfies ThemeConfig
  }, [appTheme])

  const renderEmpty = useCallback<RenderEmptyHandler>(componentName => {
    if (componentName === 'Table') {
      return <Empty image={Empty.PRESENTED_IMAGE_NO_RESULTS} />
    }
    if (componentName === 'List') {
      return <Empty image={Empty.PRESENTED_IMAGE_NO_RESULTS} />
    }

    return undefined
  }, [])

  return (
    <AntdConfigProvider
      locale={locale}
      theme={theme}
      renderEmpty={renderEmpty}
      {...props}
    />
  )
}
